/**
 * Typographic scale — the single source of truth for which roles render in the
 * Fraunces display serif and which render in the Source Sans 3 body face.
 *
 * PanLuma ships two families:
 *   --font-display  Fraunces Variable (optical-size axis)  → `font-display`
 *   --font-body     Source Sans 3                          → the document default
 *
 * Fraunces is drawn for large sizes. Applied at 14–16px it reads fussy rather
 * than elegant, so `globals.css` deliberately does NOT put it on `h1–h6`.
 * Instead each role below opts in. Rule of thumb:
 *
 *   Fraunces  → page titles, hero titles, empty-state titles, KPI numerals ≥24px
 *   Source Sans → card titles, section headings, table headers, body, everything smaller
 *
 * Consume these from the shared components (PageHeader, DashboardHero,
 * EmptyState, StatCard) so pages inherit the scale — do not re-type the class
 * strings per page. Live examples: /design-system/typography.
 * Doc: docs/design-system/ui-typography.md
 */

// =============================================================================
// DISPLAY ROLES — Fraunces
// =============================================================================

/**
 * Page title (`PageHeader` h1). 20px → 24px.
 * The smallest size at which Fraunces is used for prose.
 */
export const PAGE_TITLE =
  'text-xl sm:text-2xl font-semibold tracking-tight font-display leading-tight break-words';

/**
 * Page title pinned to a single line at 20px — a title that sits in a breadcrumb
 * trail and whose caller adds `truncate`. Same role as `PAGE_TITLE`; it just
 * can't grow at `sm:` or wrap.
 */
export const PAGE_TITLE_COMPACT =
  'text-xl font-semibold tracking-tight font-display leading-tight';

/**
 * Title of a standalone, chrome-less page — one that renders outside the app
 * shell and so has no sidebar, no `PageHeader` and no breadcrumb trail to
 * anchor it: the auth screens, the setup wizard, the customer/recruiter portal
 * landings, the careers portal, the CSAT landing, the status page.
 *
 * A step larger than `PAGE_TITLE` (24 → 30px) because on those pages the title
 * *is* the page's visual anchor rather than one element of a header row.
 * Applied by the page, since these layouts are one-offs with no shared header
 * component to own the role.
 *
 * Carries `break-words` for the same reason `PAGE_TITLE` does: several of these
 * titles interpolate unbounded tenant, company or job names, and a long
 * unbroken token would otherwise overflow the viewport at 375px.
 */
export const STANDALONE_TITLE =
  'text-2xl sm:text-3xl font-semibold tracking-tight font-display leading-tight break-words';

/** Dashboard hero title (`DashboardHero` h1). 20px → 24px. */
export const HERO_TITLE = 'text-xl sm:text-2xl font-semibold tracking-tight font-display';

/** Empty-state title (`EmptyState` h3). 18px → 20px. */
export const EMPTY_STATE_TITLE = 'text-lg sm:text-xl font-semibold font-display';

/**
 * KPI / stat numerals (`StatCard` value). The signature look.
 *
 * `tabular-nums` keeps figures the same advance width so a column of stat cards
 * stays optically aligned and a live-updating value doesn't jitter. Only for
 * standalone display numerals at ≥24px — data-table cells stay sans, where the
 * body face's figures are better tuned for dense rows.
 */
export const DISPLAY_NUMERAL = 'font-display font-semibold tabular-nums tracking-tight';

// =============================================================================
// TEXT ROLES — Source Sans 3
// =============================================================================
//
// Sans is the document default, so a text role needs NO constant and NO class:
// card titles (`CardTitle`, `CardSection`), section headings, dialog/alert/sheet
// titles, table headers, body and captions are already correct simply by not
// adding `font-display`. They differentiate by size and weight, which the
// component that owns them already sets. Do not add a constant that merely
// restates a component's own classes — it would be dead the moment the two
// drift apart.
//
// The one sans role that IS a shared pattern, because it is applied by callers
// rather than owned by a component:

/**
 * Kicker — the uppercase, tracked, muted label that names a section without
 * competing with the content below it (e.g. "Your Modules", "Recent Activity").
 * Prefer this over a same-size-as-the-card-title heading: it separates the
 * *label* layer from the *content* layer of the type hierarchy.
 */
export const KICKER = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
